import { useState } from "react";
import { Link } from "react-router-dom";
import { X, Minus, Plus, Trash2, Sparkles } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api, formatPrice } from "@/lib/api";
import { resolveImageSrc } from "@/components/SmartImage";
import { computeTotals } from "@/lib/discount";
import { openWhatsappWindow } from "@/lib/whatsapp";

const CartSheet = () => {
  const { items, open, setOpen, removeItem, updateQty, subtotal, clear, coupon, clearCoupon } = useCart();
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const handleCheckout = async () => {
    if (items.length === 0) return;
    // Reserve a popup window SYNCHRONOUSLY while we still have the user
    // gesture — iOS Safari blocks any window.open called after an await.
    const popup = openWhatsappWindow();
    setSubmitting(true);
    try {
      const payload = {
        coupon_code: coupon?.code || undefined,
        items: items.map((it) => ({
          name: it.name,
          quantity: it.quantity,
          price: it.price,
          variation: it.variationLabel || undefined,
          url: it.url,
          slug: it.slug,
          image: it.image,
        })),
      };
      const res = await api.checkoutWhatsapp(payload);
      popup.navigate(res.whatsapp_url);
      // Once the cart heads to WhatsApp the coupon has been redeemed
      if (coupon) clearCoupon();
    } catch (e) {
      console.error("[checkout] failed:", e);
      popup.abort();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70]" data-testid="cart-sheet">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => setOpen(false)}
      />
      <aside className="absolute top-0 right-0 h-full w-full sm:w-[460px] bg-white shadow-2xl flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-black/5">
          <p className="font-serif-display text-2xl">Your Edit</p>
          <button onClick={() => setOpen(false)} data-testid="close-cart" aria-label="Close cart">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-10 text-center text-neutral-500">
              <p className="font-serif-display text-2xl text-neutral-700">Your bag is empty</p>
              <p className="text-sm mt-2">Discover pieces worth coveting.</p>
            </div>
          ) : (
            <ul className="divide-y divide-black/5">
              {items.map((it) => (
                <li key={it.key} className="p-5 flex gap-4" data-testid={`cart-line-${it.id}`}>
                  <div className="w-20 h-20 bg-neutral-100 flex-shrink-0 overflow-hidden">
                    {it.image && (
                      <img src={resolveImageSrc(it.image)} alt={it.name} className="w-full h-full object-contain p-1" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium line-clamp-2">{it.name}</p>
                    {it.variationLabel && (
                      <p className="text-xs text-neutral-500 mt-1">{it.variationLabel}</p>
                    )}
                    <p className="text-sm mt-1">{formatPrice(it.price)}</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-black/10">
                        <button
                          className="px-2 py-1"
                          onClick={() => updateQty(it.key, it.quantity - 1)}
                          data-testid={`qty-dec-${it.id}`}
                          aria-label="Decrease quantity"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="px-3 text-sm" data-testid={`qty-${it.id}`}>{it.quantity}</span>
                        <button
                          className="px-2 py-1"
                          onClick={() => updateQty(it.key, it.quantity + 1)}
                          data-testid={`qty-inc-${it.id}`}
                          aria-label="Increase quantity"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(it.key)}
                        className="text-neutral-400 hover:text-black"
                        data-testid={`remove-${it.id}`}
                        aria-label="Remove item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-black/5 p-5 space-y-3">
            {(() => {
              const t = computeTotals(items);
              return (
                <>
                  {/* Bulk-discount nudge / unlocked banner */}
                  {t.tier ? (
                    <div
                      className="flex items-center gap-2 bg-black text-white px-3 py-2 text-xs"
                      data-testid="discount-banner-active"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      <span>
                        {t.qty}-piece bulk discount unlocked · {t.tier.percent}% off
                      </span>
                    </div>
                  ) : t.next ? (
                    <div
                      className="text-xs text-neutral-600 bg-neutral-50 px-3 py-2"
                      data-testid="discount-banner-next"
                    >
                      Add <strong>{t.next.min - t.qty}</strong> more piece
                      {t.next.min - t.qty > 1 ? "s" : ""} for {t.next.percent}% off your bag
                    </div>
                  ) : null}

                  {/* Comeback coupon (from abandoned-cart email link) */}
                  {coupon && (
                    <div
                      className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs"
                      data-testid="coupon-banner"
                    >
                      <Sparkles className="w-3.5 h-3.5 text-emerald-700" />
                      <span className="text-emerald-900">
                        Coupon <strong>{coupon.code}</strong> applied — extra{" "}
                        <strong>{coupon.percent_off}% off</strong>
                      </span>
                      <button
                        onClick={clearCoupon}
                        className="ml-auto text-emerald-700 hover:text-black"
                        aria-label="Remove coupon"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}

                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-500">Subtotal</span>
                    <span data-testid="cart-subtotal">{formatPrice(t.subtotal)}</span>
                  </div>
                  {t.discountPercent > 0 && (
                    <div className="flex justify-between text-sm text-green-700">
                      <span>Bulk discount ({t.discountPercent}%)</span>
                      <span data-testid="cart-discount">
                        -{formatPrice(t.discountAmount)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-base font-medium border-t border-black/5 pt-2">
                    <span>Total</span>
                    <span data-testid="cart-total">{formatPrice(t.total)}</span>
                  </div>
                </>
              );
            })()}

            <Link
              to="/checkout"
              onClick={() => setOpen(false)}
              className="btn-ink w-full block text-center"
              data-testid="checkout-cta"
            >
              Checkout
            </Link>
            <button
              onClick={handleCheckout}
              disabled={submitting}
              className="btn-outline w-full disabled:opacity-60 text-xs"
              data-testid="whatsapp-checkout-button"
            >
              {submitting ? "Preparing…" : "Order via WhatsApp"}
            </button>
            <button
              onClick={clear}
              className="w-full text-xs text-neutral-500 hover:text-black underline-offset-4 hover:underline pt-1"
              data-testid="clear-cart"
            >
              Clear bag
            </button>
            <p className="text-[11px] text-neutral-500 text-center pt-2">
              Received a previous order?{" "}
              <Link
                to="/returns"
                onClick={() => setOpen(false)}
                className="underline-offset-4 hover:underline"
                data-testid="cart-returns-link"
              >
                Start a return →
              </Link>
            </p>
          </div>
        )}
      </aside>
    </div>
  );
};

export default CartSheet;
